import { useEffect, useMemo, useRef, useState } from "react";
import "./PropertyCustomizePost.scss";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { PropertyApi } from "../../../../utils/api/Property";
import { clientRoutes } from "../../../../utils/routes";
import { CommonHelpers } from "../../../../utils/helpers/common.helpers";
import PreviewModal from "../propertypost/view/PreviewModal";

import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import PreviewCircleIcon from "../../../../components/icons/PreviewCircleIcon";
import DownloadIcon from "../../../../components/icons/DownloadIcon";
import clsx from "clsx";
import Loading from "../../../loading/Loading";
import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import PhotoIcon from "../../../../components/icons/PhotoIcon";

const Switch = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) => {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="slider" />
    </label>
  );
};

const BASE_URL = process.env.REACT_APP_API_ENDPOINT;

type PropertyCustomizePostProps = {
  propertyPostId: string;
  postType?: string;
  subscriptionType?: string;
  onBackToConfigure?: () => void;
  onSaved?: () => void;
};

const PropertyCustomizePost = ({
  propertyPostId,
  postType = "image_post",
  subscriptionType = "",
  onBackToConfigure,
  onSaved,
}: PropertyCustomizePostProps) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [propertyPost, setPropertyPost] = useState<any | null>(null);

  const [title, setTitle] = useState("Property Walkthrough");
  const [descriptionEnabled, setDescriptionEnabled] = useState(true);
  const [description, setDescription] = useState(
    "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged.",
  );

  const [showPhotoLibrary, setShowPhotoLibrary] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string>("");

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const backToList = () => {
    const backUrl = subscriptionType
      ? `${clientRoutes.PropertyPostList}?subscription_type=${subscriptionType}`
      : clientRoutes.PropertyPostList;

    navigate(backUrl);
  };

  const getFirstProperty = (post: any) => {
    if (!post?.property_id) return null;
    return typeof post.property_id === "object" ? post.property_id : null;
  };

  const propertyDetails = useMemo(
    () => getFirstProperty(propertyPost),
    [propertyPost],
  );

  // DEMO MODE FLAGS & MOCK IMAGES (Same as OpenHomeCustomizePost)
  const FORCE_DEMO_IMAGES = true;

  const DEMO_MAIN_PHOTO =
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1777303675/07baf300f37894f352ef35342d31b138a0b84b1d_vfk52b.jpg";

  const DEMO_DESIGN_PREVIEW =
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772794870/Frame_1261156603_urera9.png";

  const DEMO_LIBRARY_IMAGES = [
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1777303675/07baf300f37894f352ef35342d31b138a0b84b1d_vfk52b.jpg",
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772880839/50e4b8a38cedf2a298e8727b499c7b1a0d3db07a_kuwymx.jpg",
    "https://res.cloudinary.com/dxnjxjtwh/image/upload/v1772881727/9f10bdd6592a13aee11ea1a151bc0ea6bdd6c3c2_2_p8qgju.jpg",
    "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
    "https://images.pexels.com/photos/259588/pexels-photo-259588.jpeg",
    "https://images.pexels.com/photos/164558/pexels-photo-164558.jpeg",
  ];

  const mainPropertyPhoto = useMemo(() => {
    if (FORCE_DEMO_IMAGES) return DEMO_MAIN_PHOTO;

    const raw = propertyDetails?.thumbnail_image_ref?.compressed_file_path;
    if (!raw) return "/images/no-image.png";
    return `${BASE_URL}/${raw}`;
  }, [propertyDetails]);

  const generatedPreviewUrl = useMemo(() => {
    if (FORCE_DEMO_IMAGES) return DEMO_DESIGN_PREVIEW;

    const activity = propertyPost?.image_post_design_activity_ref;
    const out = activity?.design_task_id?.output_image_path;
    if (!out) return "";
    return CommonHelpers.getImageUrlFromImageTool({ path: out });
  }, [propertyPost]);

  const libraryImages: string[] = useMemo(() => {
    if (FORCE_DEMO_IMAGES) return DEMO_LIBRARY_IMAGES;

    return [
      mainPropertyPhoto,
      "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
      "https://as2.ftcdn.net/jpg/02/61/89/29/1000_F_261892957_6jyBXvEgM79iYr1eEiJKCosnVPJdvHHr.jpg",
      "https://images.pexels.com/photos/259588/pexels-photo-259588.jpeg",
      "https://images.pexels.com/photos/164558/pexels-photo-164558.jpeg",
      "https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg",
    ].filter(Boolean);
  }, [mainPropertyPhoto]);

  const fetchPropertyPostDetails = async () => {
    if (!propertyPostId) return;

    setLoading(true);
    try {
      const res = await PropertyApi.getPropertyPostById(propertyPostId);
      if (res?.data?.success) {
        const data = res.data.data;
        setPropertyPost(data);

        setTitle(data?.custom_title || data?.title || "Property Walkthrough");

        if (typeof data?.description_enabled === "boolean") {
          setDescriptionEnabled(data.description_enabled);
        }

        setDescription(
          data?.custom_description ||
            data?.description ||
            "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
        );

        setSelectedPhotoUrl(
          FORCE_DEMO_IMAGES
            ? DEMO_MAIN_PHOTO
            : data?.custom_main_photo_url ||
                data?.main_photo_url ||
                mainPropertyPhoto,
        );
      } else {
        toast.error(res?.data?.message || "Failed to load customize data");
      }
    } catch (e) {
      console.error(e);
      toast.error("Unable to load customize page");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPropertyPostDetails();
  }, [propertyPostId]);

  const handleOpenPreview = () => {
    const img = generatedPreviewUrl || selectedPhotoUrl || mainPropertyPhoto;
    if (!img) return;
    setPreviewImages([img]);
    setPreviewIndex(0);
    setIsPreviewOpen(true);
  };

  const handleDownload = () => {
    const url = generatedPreviewUrl || selectedPhotoUrl || mainPropertyPhoto;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = "customize-post.png";
    a.click();
  };

  const handleSave = async () => {
    if (!propertyPostId) {
      toast.error("Missing property_post_id");
      return;
    }

    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (descriptionEnabled && !description.trim()) {
      toast.error("Description is required");
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        post_type: postType,
        custom_title: title,
        description_enabled: descriptionEnabled,
        custom_description: descriptionEnabled ? description : "",
        custom_main_photo_url: selectedPhotoUrl,
        finalize: true,
      };

      const res = await PropertyApi.updatePropertyPost(propertyPostId, payload);

      if (res?.data?.success) {
        toast.success("Saved successfully!");
        await fetchPropertyPostDetails();
        onSaved?.();
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onBackToConfigure) return onBackToConfigure();
    backToList();
  };

  if (loading) {
    return (
      <div className="customize-post-page">
        <div className="customize-loader">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="customize-post-page">
          <div
            className="propertie-view-back-btn"
            onClick={() => {
              if (onBackToConfigure) return onBackToConfigure();
              backToList();
            }}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back</p>
          </div>

          <div className="customize-layout">
            <div className="customize-left">
              <div className="customize-header">
                <h1 className="customize-title">Customize Post</h1>
                <p className="customize-subtitle">
                  Adjust the text, images, and parameters below to refine your
                  post design
                </p>
              </div>

              <div className="field">
                <label className="label">Title*</label>
                <input
                  className="input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Property Walkthrough"
                />
              </div>

              <div className="field">
                <div className="label-row">
                  <label className="label">Description*</label>
                  <Switch
                    checked={descriptionEnabled}
                    onChange={(v) => setDescriptionEnabled(v)}
                  />
                </div>

                <textarea
                  className="textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!descriptionEnabled}
                />
              </div>

              <div className="field">
                <label className="label">Main photo</label>

                <div className="main-photo-card">
                  <img
                    src={selectedPhotoUrl || mainPropertyPhoto}
                    alt="Main"
                    onError={(e: any) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/no-image.png";
                    }}
                  />
                </div>

                <div className="photo-actions">
                  <button
                    type="button"
                    className="photo-action-btn"
                    onClick={() => setShowPhotoLibrary((p) => !p)}
                  >
                    <PhotoIcon /> Change photo
                  </button>

                  <button
                    type="button"
                    className="photo-action-btn"
                    onClick={handleOpenPreview}
                  >
                    <PreviewCircleIcon /> Preview
                  </button>

                  <button
                    type="button"
                    className="photo-action-btn"
                    onClick={handleDownload}
                  >
                    <DownloadIcon /> Download
                  </button>
                </div>

                {showPhotoLibrary && (
                  <div className="photo-library">
                    <div className="photo-library-header">
                      <div className="photo-library-title">Change photo</div>
                      <button
                        className="photo-library-cancel"
                        type="button"
                        onClick={() => setShowPhotoLibrary(false)}
                      >
                        Cancel
                      </button>
                    </div>

                    <p className="photo-library-desc">
                      Select an image from your library to replace the current
                      property photo
                    </p>

                    <div className="photo-grid">
                      {libraryImages.map((url, idx) => (
                        <button
                          key={`${url}-${idx}`}
                          type="button"
                          className={clsx("photo-item", {
                            active: url === selectedPhotoUrl,
                          })}
                          onClick={() => {
                            setSelectedPhotoUrl(url);
                            setShowPhotoLibrary(false);
                          }}
                        >
                          <img src={url} alt="option" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bottom-actions">
                <button
                  type="button"
                  className="save-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>

                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>

            <div className="customize-right">
              <div className="design-preview-card">
                <div className="design-preview-img">
                  <img
                    src={
                      generatedPreviewUrl ||
                      selectedPhotoUrl ||
                      mainPropertyPhoto
                    }
                    alt="Design Preview"
                    onError={(e: any) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/images/no-image.png";
                    }}
                  />
                </div>

                <div className="design-preview-actions">
                  <button
                    type="button"
                    className="outline-action"
                    onClick={handleOpenPreview}
                  >
                    <PreviewCircleIcon /> Preview
                  </button>
                  <button
                    type="button"
                    className="outline-action"
                    onClick={handleDownload}
                  >
                    <DownloadIcon /> Download
                  </button>
                </div>
              </div>
            </div>
          </div>

          {isPreviewOpen && (
            <PreviewModal
              open={isPreviewOpen}
              onClose={() => setIsPreviewOpen(false)}
              images={previewImages}
              initialIndex={previewIndex}
            />
          )}
        </div>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default PropertyCustomizePost;
